import { MultiSelectMenu, OptionType } from '@fullstory/components';
import * as vars from '@fullstory/proto/fs/api/vars';
import { SpecObjectProperty } from '@fullstory/proto/fs/services/search-settings';
import {
  allMappedSpecObjectPropertiesBySlug,
  allSpecObjectPropertiesBySlug,
  allSpecObjectsBySlug,
  BySlug,
} from '@fullstory/redux';
import { FieldScope } from '@fullstory/search-vars';
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useSelectedFsSpecSlugs } from '../../data-mgmt/hooks/useSelectedFsSpecObjectSlugs';
import { PropertyRow } from '../../data-mgmt/types';
import css from './index.module.scss';

interface PropertyRoleSelectProps {
  property: PropertyRow;
  parentSlugFilter: string[];
  onApply: (
    property: PropertyRow,
    existingSpecPropertySlugs: string[],
    updatedSpecPropertySlugs: string[],
  ) => void;
  disabled?: boolean;
}

export const PropertyRoleSelect: React.FC<PropertyRoleSelectProps> = ({
  property,
  parentSlugFilter,
  onApply,
  disabled,
}) => {
  const intl = useIntl();
  const specObjectsBySlug = useSelector(allSpecObjectsBySlug);
  const specPropertiesBySlug = useSelector(allSpecObjectPropertiesBySlug);
  const mappedSpecPropertiesBySlug = useSelector(allMappedSpecObjectPropertiesBySlug);

  const specPropertyMeta = useCallback(
    (specProp: SpecObjectProperty | undefined) => {
      if (!specProp) {
        return {
          active: false,
          displayName: '',
          group: '',
          hasMapping: false,
        };
      }
      const lastIdx = specProp.slug.lastIndexOf('/');
      const parentSlug = lastIdx >= 0 ? specProp.slug.substring(0, lastIdx) : '';
      const parent = specObjectsBySlug[parentSlug];
      return {
        active: !!parent?.active && !specProp.metadata?.deprecated,
        displayName: specProp?.metadata?.displayName || specProp?.metadata?.name || '',
        group: parent?.displayName || '',
        hasMapping: specProp ? !!mappedSpecPropertiesBySlug[specProp.slug] : false,
      };
    },
    [specObjectsBySlug, mappedSpecPropertiesBySlug],
  );

  const isExistingMapping = useCallback(
    (specProp: SpecObjectProperty): boolean => {
      const mapping = mappedSpecPropertiesBySlug[specProp.slug];
      return (
        !!mapping &&
        property.propertyType !== FieldScope.NoScope &&
        mapping.targetPropertyScope === vars.Scope[property.propertyType] &&
        mapping.targetPropertyName === property.displayName &&
        mapping.targetPropertyType === vars.Type[property.dataType]
      );
    },
    [mappedSpecPropertiesBySlug],
  );

  const availableSpecPropertiesBySlug = useMemo(() => {
    const available: BySlug<SpecObjectProperty> = {};
    const parentSlugFilterSet = new Set<string>(parentSlugFilter);
    Object.entries(specPropertiesBySlug).forEach(([slug, specProp]) => {
      const lastIdx = specProp.slug.lastIndexOf('/');
      const parentSlug = lastIdx >= 0 ? specProp.slug.substring(0, lastIdx) : '';
      if (
        parentSlugFilterSet.has(parentSlug) &&
        specProp.propertyTypes.includes(vars.Type[property.dataType])
      ) {
        available[slug] = specProp;
      }
    });
    return available;
  }, [specPropertiesBySlug, parentSlugFilter, property.dataType]);
  const { selectedSpecSlugs, setSelectedSpecSlugs, fsSpecRoleOptions } =
    useSelectedFsSpecSlugs<SpecObjectProperty>(
      availableSpecPropertiesBySlug,
      null,
      specPropertyMeta,
      isExistingMapping,
    );

  const handleChange = useCallback(
    (opts: OptionType<string>[]) => {
      const existingSlugs = selectedSpecSlugs?.map(opt => opt.value) ?? [];
      const updatedSlugs = opts.map(opt => opt.value);
      onApply(property, existingSlugs, updatedSlugs);
      setSelectedSpecSlugs(opts);
    },
    [property, selectedSpecSlugs, setSelectedSpecSlugs, onApply],
  );

  return (
    <MultiSelectMenu
      data-component="PropertyRoleSelect"
      selected={selectedSpecSlugs ?? []}
      options={fsSpecRoleOptions}
      onChange={handleChange}
      placeholder={
        fsSpecRoleOptions.length === 0
          ? intl.formatMessage({ defaultMessage: 'No options available', id: 'Z+95A6' })
          : undefined
      }
      disabled={disabled || fsSpecRoleOptions.length === 0}
      size="x-small"
      menuHeight={385}
      variant="fill-builder"
      filterable
      preventOverflow={16}
      buttonClassName={css.propertyRuleSelectButton}
      hoistSelected={false}
    />
  );
};
